/**
 * Back-office Commercial B2B — Vue principale.
 *
 * Organisée en 6 onglets :
 *   1. Tableau de bord  : KPIs + derniers leads
 *   2. Leads            : demandes /prestations-pro (à importer dans le CRM)
 *   3. Clients          : carnet d'adresses unifié
 *   4. Devis            : création + envoi PDF par email
 *   5. Factures         : conversion depuis devis + envoi + suivi paiement
 *   6. Contrats         : modèle type ECSR + envoi
 *
 * Toutes les actions consomment l'API `/api/admin/b2b/*` (admin only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Briefcase, Inbox, Users, FileText, Receipt, FileSignature,
  TrendingUp, Clock, CheckCircle2, Euro, Plus, Search,
} from 'lucide-react';
import api from '../../api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { B2BLeadsTab } from './b2b/B2BLeadsTab';
import { B2BClientsTab } from './b2b/B2BClientsTab';
import { B2BDocumentsTab } from './b2b/B2BDocumentsTab';
import { B2BFlyersTab } from './b2b/B2BFlyersTab';
import { B2BAgendaTab } from './b2b/B2BAgendaTab';

const fmtEur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

const KpiCard = ({ icon: Icon, label, value, accent = 'primary', testid }) => (
  <Card data-testid={testid} className="overflow-hidden">
    <CardContent className="p-4 flex items-center gap-3">
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0
        ${accent === 'success' ? 'bg-emerald-500/10 text-emerald-600' :
          accent === 'warning' ? 'bg-amber-500/10 text-amber-600' :
          accent === 'info' ? 'bg-cyan-500/10 text-cyan-600' :
          'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}
      >
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground truncate">{label}</div>
        <div className="text-xl font-black mt-0.5">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = ({ stats, onGoTab }) => {
  if (!stats) {
    return <div className="text-sm text-muted-foreground p-8 text-center">Chargement des statistiques…</div>;
  }
  return (
    <div className="space-y-6" data-testid="b2b-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Inbox} label="Leads à traiter" value={stats.leads_new} accent="warning" testid="b2b-kpi-leads" />
        <KpiCard icon={Users} label="Clients" value={stats.clients_total} accent="info" testid="b2b-kpi-clients" />
        <KpiCard icon={FileText} label="Devis envoyés" value={stats.devis_sent} testid="b2b-kpi-devis-sent" />
        <KpiCard icon={CheckCircle2} label="Devis acceptés (mois)" value={stats.devis_accepted_month} accent="success" testid="b2b-kpi-devis-accepted" />
        <KpiCard icon={Receipt} label="Factures envoyées" value={stats.factures_sent} testid="b2b-kpi-factures-sent" />
        <KpiCard icon={Euro} label="Factures payées (mois)" value={stats.factures_paid_month} accent="success" testid="b2b-kpi-factures-paid" />
        <KpiCard icon={TrendingUp} label="CA du mois" value={fmtEur(stats.ca_month)} accent="success" testid="b2b-kpi-ca-month" />
        <KpiCard icon={Clock} label="CA en attente" value={fmtEur(stats.ca_pending)} accent="warning" testid="b2b-kpi-ca-pending" />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-black uppercase tracking-widest text-muted-foreground">Derniers leads</div>
              <div className="text-xs text-muted-foreground mt-1">Demandes reçues via le formulaire /prestations-pro</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onGoTab('leads')} data-testid="b2b-dashboard-see-leads">
              Voir tous les leads
            </Button>
          </div>
          {(!stats.latest_leads || stats.latest_leads.length === 0) ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Aucun lead pour le moment.</div>
          ) : (
            <div className="space-y-2">
              {stats.latest_leads.slice(0, 5).map((l) => (
                <div key={l.id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{l.school_name || '—'} <span className="text-muted-foreground font-normal">· {l.city || '—'}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.contact_name} · {l.contact_email}</div>
                  </div>
                  <div className="text-[10px] text-muted-foreground ml-2 whitespace-nowrap">
                    {l.status === 'imported' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 font-bold">Importé</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 font-bold">À traiter</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default function AdminB2B() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'dashboard';
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const loadStats = async () => {
    try {
      const s = await api.b2bGetStats();
      setStats(s);
    } catch (e) {
      toast.error("Impossible de charger les statistiques B2B");
    }
  };

  useEffect(() => { loadStats(); }, []);

  const handleTab = (next) => {
    setTab(next);
    setSearchParams(next === 'dashboard' ? {} : { tab: next });
    if (next === 'dashboard') loadStats();
  };

  return (
    <div className="space-y-6" data-testid="admin-b2b-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0">
          <Briefcase size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black leading-tight">Back-office Commercial B2B</h1>
          <p className="text-sm text-muted-foreground">
            Gérez vos auto-écoles clientes, devis, factures, contrats et envois par email.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTab}>
        <TabsList className="flex flex-wrap h-auto p-1">
          <TabsTrigger value="dashboard" data-testid="b2b-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="b2b-tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="leads" data-testid="b2b-tab-leads">
            Leads
            {stats?.leads_new > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-black">
                {stats.leads_new}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="b2b-tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="devis" data-testid="b2b-tab-devis">Devis</TabsTrigger>
          <TabsTrigger value="factures" data-testid="b2b-tab-factures">Factures</TabsTrigger>
          <TabsTrigger value="contrats" data-testid="b2b-tab-contrats">Contrats</TabsTrigger>
          <TabsTrigger value="flyers" data-testid="b2b-tab-flyers">Flyers</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <Dashboard stats={stats} onGoTab={handleTab} />
        </TabsContent>

        <TabsContent value="agenda" className="mt-6">
          <B2BAgendaTab />
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <B2BLeadsTab onImported={() => { loadStats(); }} onGoToClient={() => handleTab('clients')} />
        </TabsContent>

        <TabsContent value="clients" className="mt-6">
          <B2BClientsTab onCreateDocument={() => handleTab('devis')} />
        </TabsContent>

        <TabsContent value="devis" className="mt-6">
          <B2BDocumentsTab docType="devis" onChanged={loadStats} />
        </TabsContent>

        <TabsContent value="factures" className="mt-6">
          <B2BDocumentsTab docType="facture" onChanged={loadStats} />
        </TabsContent>

        <TabsContent value="contrats" className="mt-6">
          <B2BDocumentsTab docType="contrat" onChanged={loadStats} />
        </TabsContent>

        <TabsContent value="flyers" className="mt-6">
          <B2BFlyersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
